import { useEffect, useState } from "react";
import clsx from "clsx";
import QRCode from "qrcode";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Download, KeyRound, Loader2, Mail, ShieldCheck, ShieldOff, Trash2, UserPlus, KeySquare } from "lucide-react";
import { api, ApiError, SCOPES, type Role, type Scope, type User } from "../lib/api";
import { SCOPE_HELP, SCOPE_WHO } from "./IntegrationSettings";

const field = "w-full rounded-md border border-line bg-panel px-2.5 py-2 text-[14px]";
const primary = "rounded-lg bg-ink px-3 py-2 text-[13.5px] font-bold text-panel disabled:opacity-60";
const secondary = "rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold hover:border-ink-3 disabled:opacity-60";

const message = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);

/** Six-digit code box: digits only, submits on the sixth digit. */
function CodeInput({ value, onChange, onComplete, autoFocus = true }: { value: string; onChange: (v: string) => void; onComplete?: (v: string) => void; autoFocus?: boolean }) {
  return (
    <input
      inputMode="numeric"
      autoComplete="one-time-code"
      autoFocus={autoFocus}
      maxLength={6}
      placeholder="123456"
      aria-label="6-digit code"
      className={clsx(field, "tabular text-center text-[22px] tracking-[0.4em]")}
      value={value}
      onChange={(e) => {
        const v = e.target.value.replace(/\D/g, "").slice(0, 6);
        onChange(v);
        if (v.length === 6) onComplete?.(v);
      }}
    />
  );
}

/** Second sign-in step: a code from the authenticator app, or a recovery code. */
export function MfaCodeStep({ email, onRestart }: { email: string; onRestart: () => void }) {
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState<number | null>(null);

  const submit = async (input: { code?: string; recoveryCode?: string }) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.mfaVerify(input);
      if (r.recoveryCodesLeft != null) {
        // Say how many are left before moving on, so nobody runs out unnoticed.
        setLeft(r.recoveryCodesLeft);
        return;
      }
      await qc.invalidateQueries();
    } catch (err) {
      setError(message(err, "Could not check the code"));
      setCode("");
      if (err instanceof ApiError && err.status === 401 && /Sign in again/.test(err.message)) onRestart();
    } finally {
      setBusy(false);
    }
  };

  if (left != null) {
    return (
      <div className="space-y-3 rounded-xl border border-line bg-panel p-4">
        <div className="text-[15px] font-bold">Signed in with a recovery code</div>
        <p className="text-[13px] text-ink-2">
          You have <b>{left}</b> recovery code{left === 1 ? "" : "s"} left. Make new ones under Settings → Sign-in &amp; users once you have your authenticator app again.
        </p>
        <button className={clsx(primary, "w-full")} onClick={() => void qc.invalidateQueries()}>
          Continue
        </button>
      </div>
    );
  }

  return (
    <form
      className="space-y-3 rounded-xl border border-line bg-panel p-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submit(recovery != null ? { recoveryCode: recovery } : { code });
      }}
    >
      <div className="flex items-center gap-2 text-[15px] font-bold">
        <ShieldCheck className="size-5 text-ok" /> Two-step sign-in
      </div>
      <p className="text-[12.5px] text-ink-3">{email}</p>
      {recovery == null ? (
        <>
          <label className="block text-[12.5px] font-semibold">
            Code from your authenticator app
            <div className="mt-1">
              <CodeInput value={code} onChange={setCode} onComplete={(v) => !busy && void submit({ code: v })} />
            </div>
          </label>
          <button type="button" className="text-[12px] text-ink-3 underline" onClick={() => setRecovery("")}>
            Lost your phone? Use a recovery code
          </button>
        </>
      ) : (
        <>
          <label className="block text-[12.5px] font-semibold">
            Recovery code
            <input autoFocus className={clsx(field, "mt-1 tabular uppercase")} placeholder="XXXX-XXXX-XX" value={recovery} onChange={(e) => setRecovery(e.target.value)} />
          </label>
          <button type="button" className="text-[12px] text-ink-3 underline" onClick={() => setRecovery(null)}>
            Use the authenticator app instead
          </button>
        </>
      )}
      {error && <div className="text-[12.5px] text-bad">{error}</div>}
      <div className="flex gap-2">
        <button type="button" className={secondary} onClick={onRestart}>
          Back
        </button>
        <button disabled={busy || (recovery == null ? code.length !== 6 : recovery.trim().length < 10)} className={clsx(primary, "flex-1")}>
          {busy ? "Checking…" : "Verify"}
        </button>
      </div>
    </form>
  );
}

/** Scan a QR code, confirm with a code, then save the recovery codes. */
export function MfaSetup({ onDone, intro }: { onDone: () => void; intro?: React.ReactNode }) {
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string; qr: string } | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .mfaSetup()
      .then(async (s) => {
        const qr = await QRCode.toDataURL(s.otpauthUrl, { margin: 1, width: 220 });
        if (live) setSetup({ ...s, qr });
      })
      .catch((err) => live && setError(message(err, "Could not start setup")));
    return () => {
      live = false;
    };
  }, []);

  const confirm = async (c: string) => {
    setBusy(true);
    setError(null);
    try {
      setCodes((await api.mfaEnable(c)).recoveryCodes);
    } catch (err) {
      setError(message(err, "Could not check the code"));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  if (codes) return <RecoveryCodes codes={codes} onDone={onDone} />;

  return (
    <div className="space-y-3">
      {intro}
      <ol className="list-decimal space-y-1 pl-5 text-[12.5px] text-ink-2">
        <li>Open an authenticator app on your phone (Google Authenticator, Microsoft Authenticator, 1Password, Authy…).</li>
        <li>Add an account and scan this code.</li>
        <li>Type the 6-digit code the app shows.</li>
      </ol>
      <div className="grid place-items-center rounded-lg bg-white p-3">
        {setup ? <img src={setup.qr} alt="QR code for your authenticator app" className="size-[220px]" /> : <Loader2 className="my-20 size-6 animate-spin text-ink-3" />}
      </div>
      {setup && (
        <details className="text-[12px] text-ink-3">
          <summary className="cursor-pointer">Can't scan? Type this key instead</summary>
          <div className="tabular mt-1 rounded bg-panel-2 p-2 text-[13px] break-all text-ink select-all">{setup.secret.match(/.{1,4}/g)?.join(" ")}</div>
        </details>
      )}
      <CodeInput value={code} onChange={setCode} onComplete={(v) => !busy && void confirm(v)} autoFocus={false} />
      {error && <div className="text-[12.5px] text-bad">{error}</div>}
      <button disabled={busy || code.length !== 6 || !setup} className={clsx(primary, "w-full")} onClick={() => void confirm(code)}>
        {busy ? "Checking…" : "Turn on two-step sign-in"}
      </button>
    </div>
  );
}

/** Shown exactly once: the codes are stored hashed and cannot be shown again. */
export function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const text = `Vectro Allocate recovery codes (each works once)\n\n${codes.join("\n")}\n`;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-[15px] font-bold">
        <KeyRound className="size-5 text-warn" /> Save your recovery codes
      </div>
      <p className="text-[12.5px] text-ink-2">If you lose your phone, each of these codes lets you sign in once. They will not be shown again.</p>
      <div className="tabular grid grid-cols-2 gap-1.5 rounded-lg bg-panel-2 p-3 text-[14px] font-semibold">
        {codes.map((c) => (
          <div key={c}>{c}</div>
        ))}
      </div>
      <div className="flex gap-2">
        <button
          className={clsx(secondary, "flex items-center gap-1")}
          onClick={async () => {
            await navigator.clipboard.writeText(text);
            setCopied(true);
          }}
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />} {copied ? "Copied" : "Copy"}
        </button>
        <a className={clsx(secondary, "flex items-center gap-1")} href={`data:text/plain;charset=utf-8,${encodeURIComponent(text)}`} download="yellow-better-recovery-codes.txt">
          <Download className="size-4" /> Download
        </a>
      </div>
      <label className="flex items-center gap-2 text-[12.5px]">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} /> I have saved these codes somewhere safe
      </label>
      <button disabled={!saved} className={clsx(primary, "w-full")} onClick={onDone}>
        Continue
      </button>
    </div>
  );
}

/** Settings → Sign-in & users: your own two-step sign-in, and (admins) everyone's. */
export function SecuritySection({ isAdmin, isSuper = false }: { isAdmin: boolean; isSuper?: boolean }) {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ["mfa"], queryFn: api.mfaStatus });
  const users = useQuery({ queryKey: ["users"], queryFn: api.users, enabled: isAdmin });
  const [mode, setMode] = useState<null | "setup" | "codes" | "disable">(null);
  const [code, setCode] = useState("");
  const [newCodes, setNewCodes] = useState<string[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [invited, setInvited] = useState<{ id: string; message: string; link: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["mfa"] });
    void qc.invalidateQueries({ queryKey: ["users"] });
  };
  const s = status.data;
  const card = "rounded-xl border border-line p-4";

  return (
    <>
      <h2 className="mb-1 text-[18px] font-bold">Sign-in &amp; users</h2>
      <p className="mb-3 text-[12.5px] text-ink-3">Two-step sign-in asks for a code from an authenticator app after the password.</p>

      <div className={clsx(card, "mb-4 max-w-lg")}>
        <div className="mb-1 flex items-center gap-2 text-[14px] font-bold">
          {s?.enabled ? <ShieldCheck className="size-5 text-ok" /> : <ShieldOff className="size-5 text-warn" />}
          Your two-step sign-in: {s ? (s.enabled ? "on" : "off") : "…"}
        </div>
        {s?.enabled && (
          <div className="mb-2 text-[12.5px] text-ink-3">
            {s.recoveryCodesLeft} recovery code{s.recoveryCodesLeft === 1 ? "" : "s"} left.{s.required ? " Required for everyone here." : ""}
          </div>
        )}
        {!s?.enabled && mode !== "setup" && (
          <button className={primary} onClick={() => setMode("setup")}>
            Set up two-step sign-in
          </button>
        )}
        {mode === "setup" && (
          <MfaSetup
            onDone={() => {
              setMode(null);
              refresh();
            }}
          />
        )}
        {s?.enabled && mode === null && !newCodes && (
          <div className="flex flex-wrap gap-2">
            <button className={secondary} onClick={() => setMode("codes")}>
              Make new recovery codes
            </button>
            {!s.required && (
              <button className={secondary} onClick={() => setMode("disable")}>
                Turn off
              </button>
            )}
          </div>
        )}
        {(mode === "codes" || mode === "disable") && (
          <form
            className="mt-2 space-y-2"
            onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              try {
                if (mode === "codes") setNewCodes((await api.mfaRecoveryCodes(code)).recoveryCodes);
                else await api.mfaDisable(code);
                setMode(null);
                setCode("");
                refresh();
              } catch (err) {
                setError(message(err, "That did not work"));
              }
            }}
          >
            <div className="text-[12.5px] font-semibold">{mode === "codes" ? "Confirm with a code from your app (old recovery codes stop working)" : "Confirm with a code from your app"}</div>
            <CodeInput value={code} onChange={setCode} />
            {error && <div className="text-[12.5px] text-bad">{error}</div>}
            <div className="flex gap-2">
              <button type="button" className={secondary} onClick={() => setMode(null)}>
                Cancel
              </button>
              <button disabled={code.length !== 6} className={primary}>
                {mode === "codes" ? "Make new codes" : "Turn off"}
              </button>
            </div>
          </form>
        )}
        {newCodes && <RecoveryCodes codes={newCodes} onDone={() => setNewCodes(null)} />}
      </div>

      {isAdmin && <EmailCard />}

      {isAdmin && (
        <div className={card}>
          <div className="mb-2 flex items-center gap-2 text-[14px] font-bold">
            Users
            <button className={clsx(secondary, "ml-auto flex items-center gap-1")} onClick={() => setAdding((v) => !v)}>
              <UserPlus className="size-4" /> Add someone
            </button>
          </div>
          {adding && <AddUser onDone={() => { setAdding(false); refresh(); }} />}
          {users.data && (
            <p className="mb-2 text-[12px] text-ink-3">
              {users.data.mfaRequired
                ? "Two-step sign-in is required: anyone without it is asked to set it up at their next sign-in."
                : "Two-step sign-in is optional here (it is required in the published app)."}{" "}
              Resetting it signs the person out; they set up a new authenticator at their next sign-in.
            </p>
          )}
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b border-line text-left text-[11px] tracking-wide text-ink-3 uppercase">
                <th className="px-2 py-1.5">Name</th>
                <th className="px-2 py-1.5">Email</th>
                <th className="px-2 py-1.5">Role</th>
                <th className="px-2 py-1.5">Two-step</th>
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {users.data?.users.map((u) => (
                <tr key={u.id} className="border-b border-line">
                  <td className="px-2 py-1.5 font-semibold">{u.name}</td>
                  <td className="px-2 py-1.5 text-ink-2">{u.email}</td>
                  <td className="px-2 py-1.5">
                    <RolePicker user={u} isSuper={isSuper} anySuper={users.data?.users.some((x) => x.role === "superadmin") ?? true} onChanged={refresh} onError={(m) => setInvited({ id: u.id, message: m, link: null })} />
                    {u.role === "vendor" && (
                      <VendorAllowance user={u} onChanged={(m) => { if (m) setInvited({ id: u.id, message: m, link: null }); refresh(); }} onError={(m) => setInvited({ id: u.id, message: m, link: null })} />
                    )}
                  </td>
                  <td className={clsx("px-2 py-1.5", u.mfa ? "text-ok" : "text-warn")}>{u.mfa ? "On" : "Not set up"}</td>
                  <td className="px-2 py-1.5 text-right">
                    {/* A super administrator's sign-in is only another super administrator's to reset. */}
                    {u.role === "superadmin" && !isSuper ? null : (
                      <>
                        <button
                          className={clsx(secondary, "mr-1")}
                          title="Send a welcome email with a one-time link to set a password"
                          onClick={async () => {
                            const r = await api.sendInvite(u.id);
                            setInvited({ id: u.id, message: r.emailed ? `Sent to ${u.email}` : r.message, link: r.link });
                          }}
                        >
                          <Mail className="size-4" />
                        </button>
                        <button
                          className={clsx(secondary, "mr-1")}
                          title="Force a password reset: the current password stops working and a link is sent"
                          onClick={async () => {
                            if (!confirm(`Force a password reset for ${u.name}? Their password stops working at once, they are signed out everywhere, and a link is sent to ${u.email}.`)) return;
                            const r = await api.resetPassword(u.id);
                            setInvited({ id: u.id, message: r.emailed ? `Password reset, link sent to ${u.email}` : r.message, link: r.link });
                            refresh();
                          }}
                        >
                          <KeySquare className="size-4" />
                        </button>
                        {u.mfa && (
                          <button
                            className={clsx(secondary, "mr-1")}
                            title="Reset two-step sign-in"
                            onClick={async () => {
                              if (!confirm(`Reset two-step sign-in for ${u.name}? They will be signed out and set it up again at their next sign-in.`)) return;
                              await api.resetMfa(u.id);
                              refresh();
                            }}
                          >
                            <ShieldOff className="size-4" />
                          </button>
                        )}
                      </>
                    )}
                    <button
                      className={clsx(secondary, "text-bad")}
                      title="Delete this account"
                      onClick={async () => {
                        if (!confirm(`Delete ${u.name} (${u.email})? Their account and sessions go; what they changed stays in the history.`)) return;
                        try {
                          await api.deleteUser(u.id);
                          refresh();
                        } catch (err) {
                          setInvited({ id: u.id, message: message(err, "Could not delete that account"), link: null });
                        }
                      }}
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {invited && (
            <div className="mt-2 rounded-lg bg-panel-2 p-2 text-[12px]">
              {invited.message}
              {invited.link && (
                <>
                  {" "}
                  Pass this link on yourself: <span className="break-all select-all">{invited.link}</span>
                </>
              )}
            </div>
          )}
          {users.isLoading && <Loader2 className="mt-2 size-4 animate-spin text-ink-3" />}
        </div>
      )}
    </>
  );
}


const ROLE_NAMES: Array<[Role, string]> = [
  ["superadmin", "Super administrator"],
  ["admin", "Administrator"],
  ["planner", "Planner"],
  ["handler", "Ground handler"],
  ["airline", "Airline"],
  ["viewer", "Viewer"],
  ["vendor", "Vendor (API only)"],
];

/** Tick the scopes a vendor may give its keys; one label per scope, named for who uses it. */
function ScopeBoxes({ value, onChange, disabled = false }: { value: Scope[]; onChange: (next: Scope[]) => void; disabled?: boolean }) {
  return (
    <span className="flex flex-wrap gap-x-3 gap-y-1">
      {SCOPES.map((s) => (
        <label key={s} className="flex items-center gap-1 text-[12px]" title={SCOPE_HELP[s] ?? s}>
          <input type="checkbox" disabled={disabled} checked={value.includes(s)} onChange={(e) => onChange(e.target.checked ? [...value, s] : value.filter((x) => x !== s))} />
          <span>
            <b>{s}</b>
            {SCOPE_WHO[s] ? ` (${SCOPE_WHO[s]})` : ""}
          </span>
        </label>
      ))}
    </span>
  );
}

/**
 * A vendor's allowance, changed in place. Narrowing it revokes any key of theirs that
 * now reaches further than the account may, and the message says how many went.
 */
function VendorAllowance({ user, onChanged, onError }: { user: User; onChanged: (message: string | null) => void; onError: (message: string) => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="mt-1">
      <ScopeBoxes
        value={user.apiScopes}
        disabled={busy}
        onChange={async (next) => {
          if (next.length === 0) return onError("A vendor needs at least one scope; change their role instead to take the API away");
          setBusy(true);
          try {
            const r = await api.setRole(user.id, "vendor", null, next);
            onChanged(r.keysRevoked ? `${r.keysRevoked} of ${user.name}'s keys reached beyond the new allowance and ${r.keysRevoked === 1 ? "was" : "were"} revoked` : null);
          } catch (err) {
            onError(message(err, "Could not change the allowance"));
          } finally {
            setBusy(false);
          }
        }}
      />
    </div>
  );
}

/**
 * Change someone's role in place. The choices follow the server's rules: a super
 * administrator is appointed by one, except the very first, whom an administrator may
 * appoint when there is none; and a super administrator's own role is theirs to change.
 */
function RolePicker({ user, isSuper, anySuper, onChanged, onError }: { user: User; isSuper: boolean; anySuper: boolean; onChanged: () => void; onError: (message: string) => void }) {
  const [busy, setBusy] = useState(false);
  if (user.role === "superadmin" && !isSuper) return <span className="capitalize">{user.role}</span>;
  const choices = ROLE_NAMES.filter(([r]) => r !== "superadmin" || isSuper || !anySuper);
  return (
    <select
      className="rounded-md border border-line bg-panel px-1.5 py-1 text-[12px]"
      value={user.role}
      disabled={busy}
      aria-label={`Role of ${user.name}`}
      onChange={async (e) => {
        const role = e.target.value as Role;
        const airline = role === "airline" ? (user.airline ?? prompt(`Airline code for ${user.name} (ICAO, e.g. WJA)`)?.trim().toUpperCase() ?? null) : null;
        if (role === "airline" && !airline) return;
        // Someone made a vendor starts with read only; the row then offers the other scopes.
        if (user.role === "vendor" && role !== "vendor" && !confirm(`Make ${user.name} a ${ROLE_NAMES.find(([r]) => r === role)?.[1] ?? role}? Every API key they issued stops working at once.`)) return;
        setBusy(true);
        try {
          await api.setRole(user.id, role, airline, role === "vendor" ? ["read"] : undefined);
          onChanged();
        } catch (err) {
          onError(message(err, "Could not change the role"));
        } finally {
          setBusy(false);
        }
      }}
    >
      {choices.map(([r, name]) => (
        <option key={r} value={r}>
          {name}
        </option>
      ))}
    </select>
  );
}

/** Create an account and send the welcome email. */
function AddUser({ onDone }: { onDone: () => void }) {
  // Only a super administrator may create another; the server refuses it either way.
  const me = useQuery({ queryKey: ["session"], queryFn: api.session }).data?.user;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("planner");
  const [airline, setAirline] = useState("");
  const [apiScopes, setApiScopes] = useState<Scope[]>(["read"]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ message: string; link: string | null } | null>(null);
  const field = "rounded-md border border-line bg-panel px-2 py-1.5 text-[12.5px]";

  if (done) {
    return (
      <div className="mb-3 rounded-lg border border-line p-3 text-[12.5px]">
        <div className="font-semibold">{done.message}</div>
        {done.link && (
          <p className="mt-1 text-ink-2">
            Email is not set up on this server, so send them this link yourself: <span className="break-all select-all">{done.link}</span>
          </p>
        )}
        <button className={clsx(secondary, "mt-2")} onClick={onDone}>
          Done
        </button>
      </div>
    );
  }

  return (
    <form
      className="mb-3 flex flex-wrap items-end gap-2 rounded-lg border border-line p-3"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          const r = await api.addUser({ name, email, role, airline: role === "airline" ? airline.toUpperCase() : null, apiScopes: role === "vendor" ? apiScopes : undefined });
          setDone({ message: r.emailed ? `Account created. Welcome email sent to ${email}.` : `Account created. ${r.message}`, link: r.link });
        } catch (err) {
          setError(message(err, "Could not create the account"));
        } finally {
          setBusy(false);
        }
      }}
    >
      <label className="text-[11px] font-semibold text-ink-3">
        Name
        <input required className={clsx(field, "mt-0.5 block w-44")} value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="text-[11px] font-semibold text-ink-3">
        Email
        <input required type="email" className={clsx(field, "mt-0.5 block w-56")} value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      <label className="text-[11px] font-semibold text-ink-3">
        Role
        <select className={clsx(field, "mt-0.5 block")} value={role} onChange={(e) => setRole(e.target.value as Role)}>
          <option value="admin">Administrator</option>
          <option value="planner">Planner</option>
          <option value="handler">Ground handler</option>
          <option value="airline">Airline</option>
          <option value="viewer">Viewer</option>
          <option value="vendor">Vendor (API only)</option>
        </select>
      </label>
      {role === "airline" && (
        <label className="text-[11px] font-semibold text-ink-3">
          Airline code
          <input required className={clsx(field, "mt-0.5 block w-24 uppercase")} placeholder="WJA" value={airline} onChange={(e) => setAirline(e.target.value)} />
        </label>
      )}
      {role === "vendor" && (
        <div className="w-full text-[11px] font-semibold text-ink-3">
          API scopes this vendor may give its keys
          <div className="mt-1 font-normal text-ink">
            <ScopeBoxes value={apiScopes} onChange={setApiScopes} />
          </div>
          <p className="mt-1 font-normal">They sign in to a page with the API documentation and their own keys, and see nothing else.</p>
        </div>
      )}
      <button disabled={busy || (role === "vendor" && apiScopes.length === 0)} className={primary}>
        {busy ? "Creating…" : "Create and send welcome email"}
      </button>
      {error && <div className="w-full text-[12.5px] text-bad">{error}</div>}
    </form>
  );
}

/** Whether outgoing email works, with a test button. */
function EmailCard() {
  const status = useQuery({ queryKey: ["email"], queryFn: api.emailStatus });
  const [test, setTest] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const s = status.data;
  return (
    <div className="mb-4 max-w-lg rounded-xl border border-line p-4">
      <div className="mb-1 flex items-center gap-2 text-[14px] font-bold">
        <Mail className={clsx("size-5", s?.state === "ok" ? "text-ok" : s?.configured ? "text-bad" : "text-ink-3")} />
        Outgoing email: {s ? (s.configured ? (s.state === "error" ? "needs attention" : "on") : "not set up") : "…"}
      </div>
      <div className="text-[12.5px] text-ink-2">{s?.message}</div>
      {s?.configured && (
        <button
          className={clsx(secondary, "mt-2")}
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              setTest((await api.emailTest()).message);
            } catch (err) {
              setTest(message(err, "The test failed"));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Sending…" : "Send a test email to me"}
        </button>
      )}
      {test && <div className="mt-2 text-[12.5px] text-ink-2">{test}</div>}
    </div>
  );
}

/** The page an invited person lands on: choose a password, then sign in. */
export function AcceptInvite({ token, onDone }: { token: string; onDone: () => void }) {
  const invite = useQuery({ queryKey: ["invite", token], queryFn: () => api.invite(token), retry: false });
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const short = password.length < 10;

  if (invite.isLoading) return <Loader2 className="size-5 animate-spin text-ink-3" />;
  if (invite.isError)
    return (
      <div className="space-y-3 rounded-xl border border-line bg-panel p-4">
        <div className="text-[15px] font-bold">That link cannot be used</div>
        <p className="text-[13px] text-ink-2">It has already been used or has expired. Ask whoever invited you to send another.</p>
        <button className={clsx(primary, "w-full")} onClick={onDone}>
          Go to sign in
        </button>
      </div>
    );
  if (done)
    return (
      <div className="space-y-3 rounded-xl border border-line bg-panel p-4">
        <div className="text-[15px] font-bold">Password set</div>
        <p className="text-[13px] text-ink-2">Sign in with {invite.data?.email} and your new password. You will then set up two-step sign-in with an authenticator app.</p>
        <button className={clsx(primary, "w-full")} onClick={onDone}>
          Sign in
        </button>
      </div>
    );

  return (
    <form
      className="space-y-3 rounded-xl border border-line bg-panel p-4"
      onSubmit={async (e) => {
        e.preventDefault();
        setError(null);
        setBusy(true);
        try {
          await api.acceptInvite(token, password);
          setDone(true);
        } catch (err) {
          setError(message(err, "Could not set the password"));
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="text-[15px] font-bold">Welcome, {invite.data?.name}</div>
      <p className="text-[12.5px] text-ink-3">Choose a password for {invite.data?.email}. At least 10 characters; a short phrase works well.</p>
      <label className="block text-[12px] font-semibold">
        Password
        <input autoFocus type="password" autoComplete="new-password" className={clsx(field, "mt-1")} value={password} onChange={(e) => setPassword(e.target.value)} />
      </label>
      <label className="block text-[12px] font-semibold">
        Password again
        <input type="password" autoComplete="new-password" className={clsx(field, "mt-1")} value={again} onChange={(e) => setAgain(e.target.value)} />
      </label>
      {error && <div className="text-[12.5px] text-bad">{error}</div>}
      <button disabled={busy || short || password !== again} className={clsx(primary, "w-full")}>
        {busy ? "Saving…" : short ? "At least 10 characters" : password !== again ? "The two do not match" : "Set my password"}
      </button>
    </form>
  );
}
